import React, { useState, useRef, useCallback } from 'react'
import { useTranslation } from 'react-i18next'
import styled from 'styled-components'
import { InsightViewOption } from 'enums'
import messageUtil from 'utils/messageUtil'
import { Widget } from 'features/types'
import { Theme } from 'features/ui/types'
import { debounce } from 'lodash'
import { useDashboardWidgetSetting } from 'hooks/useDashboardState'
import { useResizeObserver } from 'hooks/useResizeObserver'
import { SocialWebDemographicWidget } from 'schemas/SocialWebDemographicWidgetSchema'
// @ts-ignore
import { SimpleBar } from '@netbasequid/chart'
// @ts-ignore
import { Tooltip } from '@netbasequid/core'
import { getExplanationConfigs } from 'components/visualization/Table/HeaderRenderer'
import useDemographicData from '../useDemographicData'
import TableChart from '../../../../visualization/Table'
import {
  getPercentByIndex,
  getDisplayIndex,
  getIndexValue,
  getPostsPercentage,
  getDisplayPostPercentage,
  getLabelByViewOption,
  getBarColor,
  getSortingConfigByTableSortOption
} from '../utils/index'

const StyledLabel = styled.span`
  min-width: 38px;
  text-align: right;
`

const StyledNameContainer = styled.span`
  overflow: hidden;
  text-overflow: ellipsis;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  word-break: break-word;
  overflow-wrap: break-word;
`

const NameContainer = ({ children, mode }: { children: React.ReactNode; mode: Theme }) => {
  const [displayTooltip, setDisplayTooltip] = useState(false)
  const containerRef = useRef<HTMLSpanElement>(null)

  const checkCellContent = () => {
    if (!containerRef.current) return
    const cellContent = containerRef.current
    if (!displayTooltip) {
      setDisplayTooltip(cellContent?.scrollHeight > cellContent?.clientHeight)
    }
  }

  useResizeObserver(
    containerRef,
    // eslint-disable-next-line react-hooks/exhaustive-deps
    useCallback(debounce(checkCellContent, 10), [])
  )

  return (
    <Tooltip enabled={displayTooltip} content={children} mode={mode}>
      {/* TODO: this extra span is because tooltip will overwrite child ref, remove this span when tooltip is fixed */}
      <span>
        <StyledNameContainer ref={containerRef}>{children}</StyledNameContainer>
      </span>
    </Tooltip>
  )
}

const DemographicTable: Widget = ({ widgetId }) => {
  const {
    viewOption,
    countType,
    tableSortOption
  } = useDashboardWidgetSetting<SocialWebDemographicWidget>(widgetId)
  const { insights: rowData, maxIndex } = useDemographicData(widgetId)
  const { t } = useTranslation()

  return (
    <TableChart
      rowData={rowData}
      configs={{
        colSortingConfigs: [getSortingConfigByTableSortOption(tableSortOption!)],
        keyField: 'name'
      }}
      colDefs={[
        {
          field: 'name',
          headerText: messageUtil.viewOption(viewOption!),
          cellProps: {
            textAlign: 'left',
            displayTooltip: false,
            width: '33%'
          },
          comparator: (rowA, rowB) => {
            const { PROFESSIONS, INTERESTS, ETHNICITY } = InsightViewOption
            if ([PROFESSIONS, INTERESTS, ETHNICITY].includes(viewOption!)) {
              return getLabelByViewOption(rowA.name!, viewOption!) >
                getLabelByViewOption(rowB.name!, viewOption!)
                ? 1
                : -1
            }
            return rowA.name! > rowB.name! ? 1 : -1
          },
          customCellRender: (_, { row, theme }) => (
            <NameContainer mode={theme}>
              {getLabelByViewOption(row.name!, viewOption!)}
            </NameContainer>
          ),
          sortable: true
        },
        {
          field: 'share',
          headerText: messageUtil.viewOption(countType!),
          cellProps: {
            width: '33%'
          },
          comparator: (rowA, rowB) => (rowA.share! > rowB.share! ? 1 : -1),
          customCellRender: (_, { row, theme }) => (
            <>
              <StyledLabel>{getDisplayPostPercentage(row.share!)}</StyledLabel>
              <SimpleBar
                progress={getPostsPercentage(row.share!)}
                colorSetting={getBarColor({
                  countType: countType!,
                  viewOption: viewOption!,
                  name: row.name
                })}
                mode={theme}
              />
            </>
          ),
          sortable: true
        },
        {
          field: 'index',
          headerText: `${t('widgetType.benchmark')} (${t('vsComparison')})`,
          headerProps: {
            icon: getExplanationConfigs(t('demographicWidgetTooltip.indexHeader'))
          },
          cellProps: {
            width: '33%'
          },
          comparator: (rowA, rowB) =>
            getIndexValue(rowA.index!) > getIndexValue(rowB.index!) ? 1 : -1,
          customCellRender: (_, { row, theme }) => (
            <>
              <StyledLabel>{getDisplayIndex(row.index!)}</StyledLabel>
              <SimpleBar
                progress={getPercentByIndex(row.index!, maxIndex)}
                colorSetting={getBarColor({
                  countType: countType!,
                  viewOption: viewOption!,
                  name: row.name
                })}
                mode={theme}
              />
            </>
          ),
          sortable: true
        }
      ]}
      isDisplayFoot={false}
    />
  )
}

export default DemographicTable
